/**
 * Componente ListStudent
 * Exibe lista de alunos com filtros, paginação e ações
 *
 * NOTA: as fotos são carregadas e convertidas para base64 pelo utilitário
 * `utils/photoLoader`, que também alimenta a geração de PDF (contrato e relatório).
 * Fotos legadas do Google Drive, que não liberam CORS, continuam sendo exibidas
 * direto pela URL como fallback.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import api from "../../../services/api";
import Modal from 'react-modal';
import "./ListStudents.css";
import "./ListStudentMobile.css"; // Importa estilos específicos para mobile
import "../../Shared/PhotoMobile.css"; // Adiciona estilos aprimorados para fotos em mobile
import EditStudentModal from "../Modal/EditStudent/EditStudentModal";
import Table from "../../Shared/Table";
import { FaSync, FaUserCircle } from "react-icons/fa";
import ContractView from "../Modal/ContractView/ContractView";
import ContactModal from "../Modal/ContactModal/ContactModal";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { finalizePDF } from '../../Shared/ReportGenerator';
import Cookies from 'js-cookie';
import { useSearchParams } from 'react-router-dom';
import { fetchPhotoAsBase64, getDisplayUrls, useStudentPhoto } from '../../../utils/photoLoader';

// Função para formatar data para padrão brasileiro (dd/mm/aaaa)
const formatDateToBR = (dateString) => {
  if (!dateString) return '-';
  // Divide a string de data para evitar problemas de fuso horário
  const [year, month, day] = dateString.split('-');
  const date = new Date(year, month - 1, day); // month - 1 porque o Date usa índice 0-11 para meses
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Função para formatar data e hora para padrão brasileiro
const formatDateTimeToBR = (dateTimeString) => {
  if (!dateTimeString) return '-';
  const date = new Date(dateTimeString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Componente de foto do aluno.
// Exibe o base64 gerado pelo photoLoader (o mesmo usado no PDF) e, quando a conversão
// não é possível (fotos legadas do Google Drive), cai para a URL direta.
const PhotoWithFallback = ({ photoUrl, alt, className, onError, onLoad, style }) => {
  const { src, isLoading, hasError } = useStudentPhoto(photoUrl);
  const [directUrlIndex, setDirectUrlIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  const directUrls = useMemo(() => getDisplayUrls(photoUrl), [photoUrl]);

  useEffect(() => {
    setDirectUrlIndex(0);
    setFailed(false);
  }, [photoUrl]);

  const resolvedSrc = src || (hasError ? directUrls[directUrlIndex] : null);

  if (!photoUrl || failed || isLoading || !resolvedSrc) {
    return null;
  }

  const handleImageError = () => {
    if (!src && directUrlIndex < directUrls.length - 1) {
      setDirectUrlIndex(prev => prev + 1);
      return;
    }
    console.log('Não foi possível exibir a foto do aluno:', photoUrl);
    setFailed(true);
    if (onError) onError();
  };

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
      onError={handleImageError}
      onLoad={() => onLoad && onLoad()}
      referrerPolicy="no-referrer"
    />
  );
};

function ListStudents({ hideHeader, setHideHeader, userName: propUserName }) {
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [sortColumn, setSortColumn] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showActions, setShowActions] = useState(null); // eslint-disable-line
  const [filterFullName, setFilterFullName] = useState("");
  const [inputFullName, setInputFullName] = useState(""); // Estado local para o input com debounce
  const [filterGender, setFilterGender] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const actionsMenuRef = useRef(null);
  const debounceTimeoutRef = useRef(null); // Ref para o timeout do debounce

  const [editingStudent, setEditingStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showWithoutCourse, setShowWithoutCourse] = useState(false);

  // report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportFilters, setReportFilters] = useState({
    fullName: '',
    gender: '',
    category: '',
    showDeleted: false,
    showWithoutCourse: false,
    includePhotos: true, // new toggle
  });

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfAttachmentUrl, setPdfAttachmentUrl] = useState(null);
  //modal detail
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedContactStudent, setSelectedContactStudent] = useState(null);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  // Debounce para o filtro de nome completo
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setFilterFullName(inputFullName);
      setPagination(prev => ({ ...prev, currentPage: 1 })); // Resetar página ao filtrar
    }, 500); // 500ms de delay

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [inputFullName]);

  // Sincronizar inputFullName com filterFullName quando filterFullName muda (ex: de URL params)
  useEffect(() => {
    setInputFullName(filterFullName);
  }, [filterFullName]);

  /**
   * Carrega lista de alunos com filtros aplicados
   */
  useEffect(() => {
    setIsLoading(true);
    const fetchStudents = async () => {
      setError(null);
      try {
        const withDeleted = showDeleted ? "true" : "false";
        const params = {
          page: pagination.currentPage,
          limit: pagination.itemsPerPage,
          with_deleted: withDeleted,
          sort_column: sortColumn,
          sort_order: sortOrder,
        };
        if (filterFullName) {
          params.full_name = filterFullName;
        }
        if (filterGender) {
          params.gender = filterGender;
        }
        if (filterCategory) {
          params.category = filterCategory;
        }
        if (showWithoutCourse) {
          params.without_course = true;
        }
        const response = await api.get(`/students/index`, { params });

        setStudents(response.data.data);
        setPagination(prev => ({
          currentPage: response.data.pagination.current_page,
          totalPages: response.data.pagination.last_page,
          totalItems: response.data.pagination.total,
          // Mantém o itemsPerPage escolhido pelo usuário ao invés de usar o valor da API
          itemsPerPage: prev.itemsPerPage
        }));
      } catch (error) {
        setError("Erro ao carregar os alunos. Tente novamente.");
        console.error("Erro ao carregar lista de alunos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudents();
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    showDeleted,
    sortColumn,
    sortOrder,
    filterFullName,
    filterGender,
    filterCategory,
    showWithoutCourse,
  ]);


  useEffect(() => {
    const nameFromUrl = searchParams.get('name');
    if (nameFromUrl) {
      setFilterFullName(nameFromUrl);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }

    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/index', {
          params: {
            limit: 9999,
            page: 1
          }
        });
        // Ordena as categorias por nome em ordem alfabética
        const sortedCategories = response.data.data.sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR')
        );
        setCategories(sortedCategories);
      } catch (error) {
        console.error("Erro ao carregar categorias:", error);
        // Em caso de erro ao carregar categorias, mantém um array vazio para não quebrar o select
        setCategories([]);
      }
    };

    fetchCategories();
  }, [searchParams]);

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = Number(event.target.value);
    // Usando o callback form do setPagination para garantir que estamos trabalhando com o valor mais recente
    setPagination(prev => ({ 
      ...prev, 
      itemsPerPage: newItemsPerPage, 
      currentPage: 1 
    }));
  };

  const handleEdit = async (studentId) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/student/${studentId}/index`);
      setEditingStudent(response.data.data);
      setIsModalOpen(true);
      setShowActions(null);
    } catch (error) {
      console.error("Erro ao carregar dados do aluno:", error);
      alert("Erro ao carregar dados do aluno. Por favor, tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintContract = async (studentId) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/student/${studentId}/contract`, {
        responseType: "json",
      });
      if (response.data) {
        const studentData = response.data.student;
        const poloData = response.data.polo;
        const allCategoryData = response.data.allCategory;
        setPdfUrl({ student: studentData, polo: poloData, allCategory: allCategoryData });
        setHideHeader(true); // Esconde o header ao abrir o contrato
        setIsLoading(false);
      } else {
        console.error("Nenhuma URL recebida do servidor:", response.data);
        alert("Erro ao gerar o contrato. Nenhuma URL recebida.");
      }
      setShowActions(null);
    } catch (error) {
      console.error("Erro ao imprimir contrato:", error);
      alert("Erro ao gerar o contrato. Por favor, tente novamente.");
      setIsLoading(false);
    }
  };


  const handlePrint = () => {
    const modalContent = document.querySelector('.student-details').innerHTML;

    const printStyles = `
      <style>
        .details-content { margin: 15px 0; }
        h2, h3 { margin: 15px 0; color: #333; }
        p { margin: 8px 0; }
        @media print {
          body { font-family: Arial, sans-serif; }
          .modal-buttons { display: none; }
        }
      </style>
    `;

    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Imprimir Detalhes do Aluno</title>');
    printWindow.document.write(printStyles);
    printWindow.document.write('</head><body>');
    printWindow.document.write(modalContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
    printWindow.onafterprint = () => {
      printWindow.close();
    };
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStudent(null);
  };

  const handleSaveModal = (updatedStudent) => {
    setStudents((prevStudents) =>
      prevStudents.map((student) =>
        student.id === updatedStudent.id ? updatedStudent : student
      )
    );
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const withDeleted = showDeleted ? "true" : "false";
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        with_deleted: withDeleted,
        sort_column: sortColumn,
        sort_order: sortOrder,
      };
      if (filterFullName) params.full_name = filterFullName;
      if (filterGender) params.gender = filterGender;
      if (filterCategory) params.category = filterCategory;
      if (showWithoutCourse) params.without_course = true;
      
      const response = await api.get(`/students/index`, { params });
      setStudents(response.data.data);
      setPagination(prev => ({
        currentPage: response.data.pagination.current_page,
        totalPages: response.data.pagination.last_page,
        totalItems: response.data.pagination.total,
        // Mantém o itemsPerPage escolhido pelo usuário ao invés de usar o valor da API
        itemsPerPage: prev.itemsPerPage
      }));
    } catch (error) {
      setError("Erro ao carregar os alunos. Tente novamente.");
      console.error("Erro ao atualizar lista de alunos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // report helpers
  const handleOpenReportModal = () => {
    // copy current filters into modal state
    setReportFilters({
      fullName: filterFullName,
      gender: filterGender,
      category: filterCategory,
      showDeleted,
      showWithoutCourse,
    });
    setShowReportModal(true);
    setReportProgress(0);
  };

  const handleCloseReportModal = () => {
    if (!reportLoading) {
      setShowReportModal(false);
      setReportProgress(0);
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportProgress(0);
    try {
      const params = {};
      if (reportFilters.fullName) params.full_name = reportFilters.fullName;
      if (reportFilters.gender) params.gender = reportFilters.gender;
      if (reportFilters.category) params.category = reportFilters.category;
      if (reportFilters.showDeleted) params.with_deleted = true;
      if (reportFilters.showWithoutCourse) params.without_course = true;

      const res = await api.get('/students/report', { params });
      const list = res.data.data || [];
      const total = list.length;

      // convert photos sequentially to avoid overloading
      for (let i = 0; i < list.length; i++) {
        // update progress based on completed items
        setReportProgress(Math.round(((i + 1) / total) * 100));
        if (reportFilters.includePhotos) {
          list[i].photo_base64 = await fetchPhotoAsBase64(list[i].photo_url);
        } else {
          list[i].photo_base64 = null;
        }
      }
      setReportProgress(100);

      // build PDF
      const doc = new jsPDF('landscape');
      // determine user name like Settings component does
      const userName = propUserName || Cookies.get('userName') || '';
      const filtersDesc = `Nome: ${reportFilters.fullName || 'todos'}; Gênero: ${reportFilters.gender || 'todos'}; Oficina: ${reportFilters.category || 'todas'}; Excluídos: ${reportFilters.showDeleted ? 'sim' : 'não'}; Fotos: ${reportFilters.includePhotos ? 'sim' : 'não'}`;
      // build header/body according to photo toggle
      let head, body;
      if (reportFilters.includePhotos) {
        head = [['Foto', 'ID', 'Nome', 'Telefone', 'Gênero', 'Nascimento', 'Oficinas']];
        body = list.map(s => [
          '',
          s.id,
          `${s.first_name} ${s.last_name}`,
          s.phone_number || '',
          s.gender === 'male' ? 'Masculino' : 'Feminino',
          formatDateToBR(s.birthdate),
          s.categories && s.categories.length ? s.categories.map(c => c.name).join(', ') : ''
        ]);
      } else {
        head = [['ID', 'Nome', 'Telefone', 'Gênero', 'Nascimento', 'Oficinas']];
        body = list.map(s => [
          s.id,
          `${s.first_name} ${s.last_name}`,
          s.phone_number || '',
          s.gender === 'male' ? 'Masculino' : 'Feminino',
          formatDateToBR(s.birthdate),
          s.categories && s.categories.length ? s.categories.map(c => c.name).join(', ') : ''
        ]);
      }

      // choose row height based on whether photos are included
      const minHeight = reportFilters.includePhotos ? 26 : 14;
      // @ts-ignore
      doc.autoTable({
        head,
        body,
        startY: 36,
        styles: {
          fontSize: 8,
          minCellHeight: minHeight,
          cellPadding: 4
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: reportFilters.includePhotos
          ? { 0: { cellWidth: 25 } } // photo column only
          : {},
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 0) {
            const row = list[data.row.index];
            if (row && row.photo_base64) {
              // center image vertically/horizontally inside cell
              const imgSize = 22;
              const x = data.cell.x + (data.cell.width - imgSize) / 2;
              const y = data.cell.y + (data.cell.height - imgSize) / 2;
              doc.addImage(row.photo_base64, 'JPEG', x, y, imgSize, imgSize);
            }
          }
        },

        margin: { top: 36 }
      });

      finalizePDF(doc, userName, filtersDesc, total, 'Lista de Alunos');
      doc.save('lista-alunos.pdf');

    } catch (err) {
      console.error(err);
      alert('Erro ao gerar relatório.');
    } finally {
      setReportLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination({ ...pagination, currentPage: page });
  };

  const handleSort = (column) => {
    const columnDefinition = columns.find(c => c.key === column);
    if (columnDefinition && columnDefinition.sortable) {
      if (sortColumn === column) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortColumn(null);
          setSortOrder('asc');
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortColumn(column);
        setSortOrder('asc');
      }
      setPagination({ ...pagination, currentPage: 1 });
    }
  };


  const handleDelete = async (studentId) => {
    if (window.confirm("Tem certeza que deseja excluir este aluno?")) {
      try {
        const response = await api.delete(`/student/${studentId}/delete`);
        if (response.status === 204) {
          setStudents(students.filter((student) => student.id !== studentId));
          alert("Aluno excluído com sucesso!");
          handleRefresh();
          setShowActions(null);
        } else {
          setError("Erro ao excluir o aluno. Tente novamente.");
        }
      } catch (error) {
        console.error("Erro ao excluir aluno:", error);
        setError("Erro ao excluir o aluno. Tente novamente.");
      }
    }
  };

  const handleRestore = async (studentId) => {
    try {
      const response = await api.post(`/student/${studentId}/restore`);
      if (response.status === 200) {
        setStudents((prevStudents) =>
          prevStudents.filter((student) => student.id !== studentId)
        );
        alert("Aluno restaurado com sucesso!");
        handleRefresh();
      } else {
        setError("Erro ao restaurar o aluno. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao restaurar aluno:", error);
      setError("Erro ao restaurar o aluno. Tente novamente.");
    }
  };

  const handleOpenPdfAttachment = (pdfUrl) => {

    if (pdfUrl) {
      const formattedUrl = `http://innova.test${pdfUrl}`;
      setPdfAttachmentUrl(formattedUrl);
    } else {
      alert("Nenhum PDF de anexo disponível.");
    }
  };

  const handleDetailStudent = async (studentId) => {
    setIsLoadingDetails(true);
    try {
      const response = await api.get(`/student/${studentId}/index`);
      setSelectedStudent(response.data.data);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error("Erro ao carregar detalhes do aluno:", error);
      alert("Erro ao carregar detalhes do aluno. Por favor, tente novamente.");
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleContact = async (studentId) => {
    setIsLoadingContacts(true);
    try {
      const response = await api.get(`/student/${studentId}/contacts`);
      // Os dados já vêm no formato correto, não precisamos transformá-los
      setSelectedContactStudent(response.data);
      setIsContactModalOpen(true);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
      alert("Erro ao carregar informações de contato. Por favor, tente novamente.");
    } finally {
      setIsLoadingContacts(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target)
      ) {
        setShowActions(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const columns = [    {      
      key: "photo",
      sortable: false,
      label: "Foto",
      render: (value, item) => {
        const photoUrl = item.photo_url;
        const cellId = `photo-cell-${item.id}`;
        
        return (
          <div className="table-photo-cell" id={cellId}>
            {photoUrl ? (
              <>
                <PhotoWithFallback
                  photoUrl={photoUrl}
                  alt="Foto do aluno"
                  className="student-photo"
                  onError={() => {
                    // Mostra o ícone de fallback quando todas as tentativas falharem
                    const cell = document.getElementById(cellId);
                    if (cell) {
                      const img = cell.querySelector('.student-photo');
                      const fallback = cell.querySelector('.student-photo-fallback');
                      if (img) img.style.display = 'none';
                      if (fallback) fallback.style.display = 'block';
                    }
                  }}
                  onLoad={() => {
                    // Esconde o ícone de fallback quando a imagem carrega
                    const cell = document.getElementById(cellId);
                    if (cell) {
                      const fallback = cell.querySelector('.student-photo-fallback');
                      if (fallback) fallback.style.display = 'none';
                    }
                  }}
                />
                <FaUserCircle 
                  className="student-photo-icon student-photo-fallback"
                  style={{display: 'none'}}
                />
              </>
            ) : (
              <FaUserCircle className="student-photo-icon" />
            )}
          </div>
        );
      }
    },
    {
      key: "full_name",
      sortable: true,
      label: "Nome Completo",
      render: (value, item) => `${item.first_name} ${item.last_name}`
    },
    {
      key: "phone_number",
      label: "Telefone",
      sortable: true,
      render: (phone) => {
        if (!phone) return "Não informado";
        phone = phone.replace(/\D/g, "");
        if (phone.length === 11) {
          return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
        } else if (phone.length === 10) {
          return `(${phone.slice(0, 2)}) ${phone.slice(2, 6)}-${phone.slice(6)}`;
        }
        return phone;
      }
    },
    {
      key: "gender",
      sortable: true,
      label: "Gênero",
      render: (value) => value === 'male' ? 'Masculino' : 'Feminino'
    },    {
      key: 'birthdate',
      label: 'Data de Nascimento',
      sortable: true,
      render: (date) => formatDateToBR(date)
    },
    {key: 'age', label: 'Idade', sortable: true, render: (value) => {return value ? `${value} anos` : "Não informado"}},
    {
      key: "categories",
      label: "Oficinas",
      sortable: false,
      render: (categories) => {
        if (!categories || categories.length === 0) return "Nenhuma";
        return categories.map((category) => category.name).join(", ");
      },
    },
    {
      key: "deleted_at",
      label: "Ativo",
      sortable: true,
      render: (value) => (
        <input
          type="checkbox"
          checked={!value}
          readOnly
          disabled
          title={value ? "Inativo" : "Ativo"}
        />
      )
    }
  ];

  const getActionItems = (itemId, item) => {
    const actions = [
      {
        label: "Detalhar",
        action: () => handleDetailStudent(itemId)
      },
      {
        label: "Contatar",
        action: () => handleContact(itemId)
      }
    ];

    if (item.pdf_attachment) {
      actions.push({
        label: "Abrir Laudo PCD",
        action: () => handleOpenPdfAttachment(`/storage/${item.pdf_attachment}`),
      });
    }

    if (item.deleted_at === null) {
      actions.push(
        {
          label: "Imprimir Contrato",
          action: () => handlePrintContract(itemId),
        },
        {
          label: "Editar",
          action: () => handleEdit(itemId),
        },
        {
          label: "Excluir",
          action: () => handleDelete(itemId),
        });
    } else {
      actions.push(
        {
          label: "Restaurar",
          action: () => handleRestore(itemId),
        });
    }

    return actions;
  };

  return (
    <div className="students-list-container">
      <h2>Lista de Alunos</h2>

      <div className="header-container">
        <div className="filters-container">
          <div className="filter-group">
            <fieldset>
              <legend>Nome Completo:</legend>
              <input
                type="text"
                placeholder="Filtrar por Nome Completo"
                value={inputFullName}
                onChange={(e) => setInputFullName(e.target.value)}
              />
            </fieldset>
          </div>
          <div className="filter-group">
            <fieldset>
              <legend>Gênero:</legend>
              <select
                value={filterGender}
                onChange={(e) => {
                  setFilterGender(e.target.value);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
              >
                <option value="">Todos os Gêneros</option>
                <option value="male">Masculino</option>
                <option value="female">Feminino</option>
              </select>
            </fieldset>
          </div>
          <div className="filter-group">
            <fieldset>
              <legend>Oficina:</legend>
              <select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                disabled={showWithoutCourse}
              >
                <option value="">Todas as Oficinas</option>
                <option value="none">Sem Oficina</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </fieldset>
          </div>
          <div className="checkbox-container">
            <input
              type="checkbox"
              id="show-deleted"
              checked={showDeleted}
              onChange={(e) => {
                setShowDeleted(e.target.checked);
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            />
            <label htmlFor="show-deleted">EXIBIR EXCLUIDOS</label>
          </div>
          <div className="checkbox-container">
            <input
              type="checkbox"
              id="show-without-course"
              checked={showWithoutCourse}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowWithoutCourse(checked);
                setPagination(prev => ({ ...prev, currentPage: 1 }));
                if (checked) {
                  // clear office filter when showing students without course
                  setFilterCategory("");
                }
              }}
            />
            <label htmlFor="show-without-course">EXIBIR SEM CURSO</label>
          </div>
          <button
            className="refresh-button"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <FaSync className={isLoading ? "fa-spin" : ""} />
            {isLoading ? " Carregando..." : " Atualizar"}
          </button>
          <button
            className="refresh-button"
            style={{ marginLeft: 8 }}
            onClick={handleOpenReportModal}
            disabled={reportLoading || isLoading}
          >
            📄 Gerar Relatório
          </button>
        </div>
      </div>

      <Table
        data={students}
        columns={columns}
        itemsPerPage={Number(pagination.itemsPerPage) || 10}
        loading={isLoading}
        error={error}
        isSortable={true}
        getActionItems={getActionItems}
        handleSort={handleSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        menuHeight={200}
      />

      {/* Loading overlay para ações que demoram */}
      {isLoading && (
            <LoadingSpinner />
      )}

      {pdfAttachmentUrl && (
        <div className="iframe-modal-overlay" onClick={() => setPdfAttachmentUrl(null)}>
          <div className="iframe-modal-content" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={pdfAttachmentUrl}
              width="100%"
              height="500px"
              title="PDF de Anexo"
            />
            <button onClick={() => setPdfAttachmentUrl(null)} className="iframe-close-button">
              x
            </button>
          </div>
        </div>
      )}

      {pdfUrl && (
        <div className="iframe-modal-overlay" onClick={() => { setPdfUrl(null); setHideHeader(false); }}>
          <div className="iframe-modal-content" onClick={(e) => e.stopPropagation()}>
            <ContractView student={pdfUrl.student} polo={pdfUrl.polo} allCategory={pdfUrl.allCategory} />
          </div>
        </div>
      )}

      {/* Relatório de alunos modal */}
      {showReportModal && (
        <Modal
          isOpen={showReportModal}
          onRequestClose={handleCloseReportModal}
          contentLabel="Gerar Relatório de Alunos"
          className="modal-student-details"
          overlayClassName="modal-overlay-student-details"
        >
          <div className="report-modal">
            <h2>Relatório de Alunos</h2>
            <div className="filters">
            <div className="filter-group">
              <label>Nome:</label>
              <input
                type="text"
                value={reportFilters.fullName}
                onChange={(e) => setReportFilters(f => ({ ...f, fullName: e.target.value }))}
              />
            </div>
            <div className="filter-group">
              <label>Gênero:</label>
              <select
                value={reportFilters.gender}
                onChange={(e) => setReportFilters(f => ({ ...f, gender: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="male">Masculino</option>
                <option value="female">Feminino</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Oficina:</label>
              <select
                value={reportFilters.category}
                onChange={(e) => setReportFilters(f => ({ ...f, category: e.target.value }))}
                disabled={reportFilters.showWithoutCourse}
              >
                <option value="">Todas as Oficinas</option>
                <option value="none">Sem Oficina</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="report-show-deleted"
                checked={reportFilters.showDeleted}
                onChange={(e) => setReportFilters(f => ({ ...f, showDeleted: e.target.checked }))}
              />
              <label htmlFor="report-show-deleted">EXIBIR EXCLUIDOS</label>
            </div>
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="report-show-without-course"
                checked={reportFilters.showWithoutCourse}
                onChange={(e) => setReportFilters(f => ({ ...f, showWithoutCourse: e.target.checked, category: e.target.checked ? '' : f.category }))}
              />
              <label htmlFor="report-show-without-course">EXIBIR SEM CURSO</label>
            </div>
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="report-without-photos"
                checked={!reportFilters.includePhotos}
                onChange={(e) => setReportFilters(f => ({ ...f, includePhotos: !e.target.checked }))}
              />
              <label htmlFor="report-without-photos">SEM FOTOS</label>
            </div>
          </div>
          {reportLoading && (
            <div className="report-progress">
              Gerando... {reportProgress}%
            </div>
          )}
          <div className="modal-buttons" style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button
              className="btn-primary"
              onClick={handleGenerateReport}
              disabled={reportLoading}
            >
              Gerar PDF
            </button>
            <button
              className="btn-secondary"
              onClick={handleCloseReportModal}
              disabled={reportLoading}
            >
              Cancelar
            </button>
          </div>
          </div> {/* close report-modal */}
        </Modal>
      )}

      <div className="pagination">
        <button onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1}>{"<<"}</button>
        <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1}>{"<"}</button>
        <span>Página {pagination.currentPage} de {pagination.totalPages}</span>
        <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages}>{">"}</button>
        <button onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages}>{">>"}</button>
      </div>

      <div className="items-per-page-selector">
        <label htmlFor="itemsPerPage">Itens por página: </label>
        <select
          id="itemsPerPage"
          value={pagination.itemsPerPage}
          onChange={handleItemsPerPageChange}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={15}>15</option>
        </select>
      </div>

      {isModalOpen && (
        <EditStudentModal
          student={editingStudent}
          onClose={handleCloseModal}
          onSave={handleSaveModal}
        />
      )}      <Modal
        isOpen={isDetailsModalOpen}
        onRequestClose={() => setIsDetailsModalOpen(false)}
        contentLabel="Detalhes do Aluno"
        className="modal-student-details"
        overlayClassName="modal-overlay-student-details"
      >
        {isLoadingDetails ? (
          <LoadingSpinner />
        ) : (
          <div className="student-details">
            <div className="student-header">
              <div className="student-photo-container">
                {selectedStudent && (
                  selectedStudent.photo_url ? (
                    <>
                      <PhotoWithFallback
                        photoUrl={selectedStudent.photo_url}
                        alt="Foto do aluno"
                        className="student-detail-photo"
                        onError={() => {
                          // Mostra o ícone de fallback quando todas as tentativas falharem
                          const container = document.querySelector('.student-photo-container');
                          if (container) {
                            const img = container.querySelector('.student-detail-photo');
                            const fallback = container.querySelector('.student-detail-photo-fallback');
                            if (img) img.style.display = 'none';
                            if (fallback) fallback.style.display = 'block';
                          }
                        }}
                        onLoad={() => {
                          // Esconde o ícone de fallback quando a imagem carrega
                          const container = document.querySelector('.student-photo-container');
                          if (container) {
                            const fallback = container.querySelector('.student-detail-photo-fallback');
                            if (fallback) fallback.style.display = 'none';
                          }
                        }}
                      />
                      <FaUserCircle 
                        className="student-detail-photo-icon student-detail-photo-fallback" 
                        style={{display: 'none'}}
                      />
                    </>
                  ) : (
                    <FaUserCircle className="student-detail-photo-icon" />
                  )
                )}
              </div>
              <div className="student-title">
                <h2>Detalhes do Aluno</h2>
                {selectedStudent && (
                  <h3>{selectedStudent.first_name} {selectedStudent.last_name}</h3>
                )}
              </div>
              <div className="modal-buttons">
                <button onClick={handlePrint} className="print-button">
                  Imprimir
                </button>
              </div>
            </div>

            {selectedStudent && (
              <div className="details-content">
                <fieldset>
                  <legend>Informações Pessoais</legend>
                  <div className="details-row">
                    <ul>
                      <li><strong>ID:</strong> {selectedStudent.id}</li>
                      <li><strong>Nome Completo:</strong> {selectedStudent.first_name} {selectedStudent.last_name}</li>
                      <li><strong>Data de Nascimento:</strong> {formatDateToBR(selectedStudent.birthdate)}</li>
                      <li><strong>Gênero:</strong> {selectedStudent.gender === 'male' ? 'Masculino' : 'Feminino'}</li>
                    </ul>
                    <ul>
                      <li><strong>Telefone:</strong> {selectedStudent.phone_number}</li>
                      <li><strong>Status:</strong> {selectedStudent.deleted_at ? (
                          <>
                            Inativo (Excluído em: {formatDateTimeToBR(selectedStudent.deleted_at)})
                          </>
                        ) : 'Ativo'}
                      </li>
                      <li><strong>Oficinas:</strong> {selectedStudent.categories && selectedStudent.categories.length > 0 ? selectedStudent.categories.map(category => category.name).join(", ") : "Nenhuma"}</li>
                    </ul>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Filiação</legend>
                  <div className="details-row">
                    <ul>
                      <li><strong>Nome do Pai:</strong> {selectedStudent.father_name}</li>
                      <li><strong>Nome da Mãe:</strong> {selectedStudent.mother_name}</li>
                      <li><strong>Pode ir embora sozinho:</strong> {selectedStudent.can_go_home ? 'Sim' : 'Não'}</li>
                    </ul>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Endereço</legend>
                  <div className="details-row">
                    <ul>
                      <li><strong>Rua:</strong> {selectedStudent.street}</li>
                      <li><strong>Número:</strong> {selectedStudent.number}</li>
                      <li><strong>Complemento:</strong> {selectedStudent.complement}</li>
                    </ul>
                    <ul>
                      <li><strong>Bairro:</strong> {selectedStudent.neighborhood}</li>
                      <li><strong>Cidade:</strong> {selectedStudent.city}</li>
                      <li><strong>Estado:</strong> {selectedStudent.state}</li>
                      <li><strong>CEP:</strong> {selectedStudent.zip_code}</li>
                    </ul>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Responsáveis</legend>
                  {selectedStudent.responsibles && selectedStudent.responsibles.length > 0 ? (
                    <table className="responsibles-table">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Parentesco</th>
                          <th>Telefone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudent.responsibles.map((responsible) => (
                          <tr key={responsible.id}>
                            <td>{responsible.first_name} {responsible.last_name}</td>
                            <td>{responsible.relation}</td>
                            <td>{responsible.phone_number || 'Não informado'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p>Nenhum responsável cadastrado.</p>
                  )}
                </fieldset>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        student={selectedContactStudent}
        isLoading={isLoadingContacts}
      />
    </div>
  );

}

export default ListStudents;